import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { getSignedUrl, deleteFile } from '../../../lib/upload'

// ── vehicles ──────────────────────────────────────────────────
export const useVehicles = () => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['vehicles', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('vehicles')
        .select('*, profiles!primary_driver_id(id, display_name, member_color)')
        .eq('household_id', household.id)
        .order('year', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

  const create = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('vehicles')
        .insert({ ...payload, household_id: household.id, created_by: user.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const update = useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { data, error } = await supabase
        .from('vehicles')
        .update(payload)
        .eq('id', id)
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const remove = useMutation({
    mutationFn: async ({ id, photoPath }) => {
      if (photoPath) await deleteFile('household-documents', photoPath).catch(() => {})
      const { error } = await supabase.from('vehicles').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  return { ...query, create, update, remove }
}

// ── vehicle service records ────────────────────────────────────
export const useServiceRecords = (vehicleId) => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['service-records', vehicleId]

  const query = useQuery({
    queryKey: key,
    enabled:  !!vehicleId && !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('vehicle_service_records')
        .select('*')
        .eq('vehicle_id', vehicleId)
        .order('service_date', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

  const create = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('vehicle_service_records')
        .insert({ ...payload, vehicle_id: vehicleId, household_id: household.id, created_by: user.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const update = useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { error } = await supabase
        .from('vehicle_service_records')
        .update(payload)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const remove = useMutation({
    mutationFn: async ({ id, receiptPath }) => {
      if (receiptPath) await deleteFile('household-documents', receiptPath).catch(() => {})
      const { error } = await supabase.from('vehicle_service_records').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  return { ...query, create, update, remove }
}

// ── fuel logs ──────────────────────────────────────────────────
export const useFuelLogs = (vehicleId) => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['fuel-logs', vehicleId]

  const query = useQuery({
    queryKey: key,
    enabled:  !!vehicleId && !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('vehicle_fuel_logs')
        .select('*')
        .eq('vehicle_id', vehicleId)
        .order('log_date', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })

  const create = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('vehicle_fuel_logs')
        .insert({ ...payload, vehicle_id: vehicleId, household_id: household.id, created_by: user.id })
        .select()
        .single()
      if (error) throw error
      // Also update vehicle current mileage
      if (payload.mileage) {
        await supabase
          .from('vehicles')
          .update({ current_mileage: payload.mileage })
          .eq('id', vehicleId)
      }
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key })
      qc.invalidateQueries({ queryKey: ['vehicles'] })
    },
  })

  const remove = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('vehicle_fuel_logs').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  return { ...query, create, remove }
}

// ── vehicle reminders ──────────────────────────────────────────
export const useVehicleReminders = (vehicleId) => {
  const { household, user } = useAuth()
  const qc  = useQueryClient()
  const key = ['vehicle-reminders', vehicleId]

  const query = useQuery({
    queryKey: key,
    enabled:  !!vehicleId && !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('vehicle_reminders')
        .select('*')
        .eq('vehicle_id', vehicleId)
        .order('due_date', { ascending: true, nullsFirst: false })
      if (error) throw error
      return data ?? []
    },
  })

  const create = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase
        .from('vehicle_reminders')
        .insert({ ...payload, vehicle_id: vehicleId, household_id: household.id, created_by: user.id })
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const update = useMutation({
    mutationFn: async ({ id, ...payload }) => {
      const { error } = await supabase
        .from('vehicle_reminders')
        .update(payload)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const remove = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from('vehicle_reminders').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  return { ...query, create, update, remove }
}

// ── upcoming reminders (dashboard/overview) ────────────────────
export const useUpcomingVehicleReminders = (daysAhead = 30) => {
  const { household } = useAuth()
  return useQuery({
    queryKey: ['upcoming-vehicle-reminders', household?.id, daysAhead],
    enabled:  !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase.rpc('get_upcoming_vehicle_reminders', {
        p_household_id: household.id,
        p_days_ahead:   daysAhead,
      })
      if (error) throw error
      return data ?? []
    },
  })
}

export const useVehiclePhotoUrl = (photoPath) =>
  useQuery({
    queryKey: ['vehicle-photo', photoPath],
    enabled:  !!photoPath,
    queryFn:  () => getSignedUrl('household-documents', photoPath),
    staleTime: 1000 * 60 * 50,
  })
